import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { usePrayerSchedule } from '../contexts/PrayerScheduleContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRAYER_LABELS = {
  fajr: 'Fajr',
  dhuhr: 'Dhuhr',
  asr: 'Asr',
  maghrib: 'Maghrib',
  isha: 'Isha',
};

const formatTime12h = (time24) => {
  if (!time24) return '--:--';
  const [h, m] = time24.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return '--:--';
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
};

const ArabesqueFlourish = ({ color = '#C9A227' }) => (
  <>
    <div className="absolute top-0 left-0 w-8 h-8 pointer-events-none opacity-40 z-0">
      <svg viewBox="0 0 32 32" fill="none">
        <path d="M0 16 Q0 0, 16 0 M4 16 Q4 4, 16 4 M8 16 Q8 8, 16 8" stroke={color} strokeWidth="1" fill="none" strokeLinecap="round" />
      </svg>
    </div>
    <div className="absolute top-0 right-0 w-8 h-8 pointer-events-none opacity-40 z-0 scale-x-[-1]">
      <svg viewBox="0 0 32 32" fill="none">
        <path d="M0 16 Q0 0, 16 0 M4 16 Q4 4, 16 4 M8 16 Q8 8, 16 8" stroke={color} strokeWidth="1" fill="none" strokeLinecap="round" />
      </svg>
    </div>
  </>
);

const ClassicPrayerRow = () => {
  const { t } = useTranslation();
  const { nextPrayer, formattedCountdown, activeTimetable, setActiveSchedule } =
    usePrayerSchedule();

  // Fallback: if the shared context has no timetable yet (user never opened
  // /prayer-times), fetch calculated times once so we don't render "Loading"
  // indefinitely on secondary pages. This ONLY runs when the context is empty.
  const [fallbackLoaded, setFallbackLoaded] = useState(false);
  useEffect(() => {
    if (activeTimetable || fallbackLoaded) return;
    let cancelled = false;

    const load = async () => {
      let location = null;
      try {
        const saved = window.localStorage.getItem('quranplus-user-location');
        if (saved) location = JSON.parse(saved);
      } catch {}
      if (!location) location = { lat: 51.5074, lng: -0.1278 };

      try {
        const res = await axios.get(`${API}/prayer-times`, {
          params: {
            latitude: location.lat || location.latitude,
            longitude: location.lng || location.longitude,
            method: 15,
          },
        });
        if (cancelled) return;
        const timings = res.data?.data?.timings;
        if (!timings) return;
        setActiveSchedule({
          activeMode: 'CALCULATION',
          activeTimetable: {
            fajr: { time: timings.Fajr },
            sunrise: { time: timings.Sunrise },
            dhuhr: { time: timings.Dhuhr },
            asr: { time: timings.Asr },
            maghrib: { time: timings.Maghrib },
            isha: { time: timings.Isha },
          },
        });
      } catch {
        // silent - the widget just stays on "Loading" until user opens prayer page
      } finally {
        if (!cancelled) setFallbackLoaded(true);
      }
    };

    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTimetable]);

  const displayName = nextPrayer
    ? PRAYER_LABELS[nextPrayer.name] || nextPrayer.name
    : t('prayer.loading', 'Loading');

  return (
    <div className="px-4 pt-0 pb-0 bg-[#F1EBDD]">
      <Link
        to="/prayer-times"
        className="relative flex items-center justify-between p-3.5 rounded-2xl bg-[#F1EBDD] border-2 border-[#C9A227] shadow-sm hover:scale-[1.01] transition-transform overflow-hidden"
        data-testid="classic-next-prayer-card"
      >
        <ArabesqueFlourish color="#C9A227" />
        <div className="flex items-center gap-3 relative z-20">
          <div className="w-10 h-10 rounded-full bg-[#0F4C3A] flex items-center justify-center border border-[#C9A227] shadow-inner">
            <Clock className="w-5 h-5 text-[#C9A227]" />
          </div>
          <div>
            <span className="text-xs font-semibold text-[#4A4A4A] uppercase tracking-wider block">
              {t('prayer.nextPrayer', 'Next Prayer')}
            </span>
            <span className="text-base font-bold font-serif text-[#0F4C3A]">
              {displayName}
            </span>
          </div>
        </div>
        <div className="text-right relative z-20">
          <span className="font-extrabold text-lg text-[#0F4C3A] block">
            {nextPrayer ? formatTime12h(nextPrayer.time) : '--:--'}
          </span>
          <span className="text-xs text-[#4A4A4A]">
            {formattedCountdown
              ? `${t('prayer.in', 'in')} ${formattedCountdown}`
              : ''}
          </span>
        </div>
      </Link>
    </div>
  );
};

export default ClassicPrayerRow;
